import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { EnquiryForm } from "@/components/EnquiryForm";
import { useConfig } from "@/context/ConfigContext";
import { waLink, telLink } from "@/config/site";
import { usePageMeta } from "@/hooks/usePageMeta";
import { WhatsAppIcon } from "@/components/WhatsAppFloat";

export default function Contact() {
  const cfg = useConfig();
  usePageMeta(
    "Contact Us | Chhannulal & Brothers (CLB)",
    "Get in touch with Chhannulal & Brothers for paints, plumbing, bathware, water tanks and building materials. Send an enquiry, chat on WhatsApp or call us."
  );

  return (
    <main>
      <section className="bg-darknavy pb-20 pt-36 text-cream lg:pb-28 lg:pt-44" data-testid="contact-hero">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <Overline dark>Contact</Overline>
            <h1 className="mt-4 font-display text-4xl font-light tracking-tight sm:text-6xl">Get in Touch</h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-cream/70">
              Tell us what you're building, renovating or sourcing. We respond on WhatsApp, phone and email.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href={waLink(cfg)} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-cta" className="flex items-center gap-2 bg-[#25D366] px-6 py-4 font-display text-sm font-semibold uppercase tracking-[0.15em] text-white transition-transform duration-300 hover:scale-[1.03]">
                <WhatsAppIcon className="h-4 w-4" /> Chat on WhatsApp
              </a>
              <a href={telLink(cfg)} data-testid="contact-call-cta" className="flex items-center gap-2 border border-cream/40 px-6 py-4 font-display text-sm font-semibold uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:border-gold hover:text-gold">
                <Phone className="h-4 w-4" /> Call Us
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-20 lg:py-28" data-testid="contact-body">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-14 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <Reveal>
                <Overline>Send an enquiry</Overline>
                <h2 className="mt-4 font-display text-3xl font-light tracking-tight text-darknavy">We usually respond quickly</h2>
                <div className="mt-10">
                  <EnquiryForm />
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.1}>
                <div className="bg-darknavy p-8 text-cream lg:p-10" data-testid="contact-info-panel">
                  <h2 className="font-display text-xl font-medium">Visit or reach us</h2>
                  <ul className="mt-8 space-y-6 text-sm">
                    <li className="flex items-start gap-4">
                      <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                      <div>
                        <p className="font-display text-xs font-semibold uppercase tracking-[0.2em] text-cream/50">Address</p>
                        <p className="mt-1 leading-relaxed text-cream/80" data-testid="contact-address">{cfg.address}</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-4">
                      <Phone className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                      <div>
                        <p className="font-display text-xs font-semibold uppercase tracking-[0.2em] text-cream/50">Phone</p>
                        <a href={telLink(cfg)} className="mt-1 block text-cream/80 hover:text-cream transition-colors" data-testid="contact-phone">{cfg.phone}</a>
                      </div>
                    </li>
                    <li className="flex items-start gap-4">
                      <Mail className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                      <div>
                        <p className="font-display text-xs font-semibold uppercase tracking-[0.2em] text-cream/50">Email</p>
                        <a href={`mailto:${cfg.email}`} className="mt-1 block text-cream/80 hover:text-cream transition-colors" data-testid="contact-email">{cfg.email}</a>
                      </div>
                    </li>
                    <li className="flex items-start gap-4">
                      <Clock className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                      <div>
                        <p className="font-display text-xs font-semibold uppercase tracking-[0.2em] text-cream/50">Business Hours</p>
                        <p className="mt-1 text-cream/80" data-testid="contact-hours">{cfg.hours}</p>
                      </div>
                    </li>
                  </ul>
                  <div className="mt-8 flex aspect-[4/3] items-center justify-center border border-dashed border-cream/25" data-testid="contact-map-placeholder">
                    {cfg.mapsUrl ? (
                      <iframe title="Chhannulal & Brothers location" src={cfg.mapsUrl} className="h-full w-full" loading="lazy" />
                    ) : (
                      <div className="p-6 text-center">
                        <MapPin className="mx-auto h-8 w-8 text-gold/60" />
                        <p className="mt-3 text-xs uppercase tracking-[0.2em] text-cream/40">Google Maps — add your embed link from the admin panel</p>
                      </div>
                    )}
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
